import { onUnmounted, type Ref, ref } from "vue"

// 使用安全的 setTimeout 鏈來防止請求重疊
export function usePolling<T extends unknown[]>(
    fn: (...args: T) => Promise<boolean | void>,
    interval = 5000
): {
    isPolling: Ref<boolean>
    startPolling: (...args: T) => void
    stopPolling: () => void
} {
    const isPolling = ref(false)
    const isExecuting = ref(false)
    let timerId: ReturnType<typeof setTimeout> | null = null

    // core function
    const poll = async (...args: T): Promise<void> => {
        // 防止重疊執行
        if (isExecuting.value) return

        isExecuting.value = true
        const shouldStop = await fn(...args)
        isExecuting.value = false

        if (shouldStop || !isPolling.value) {
            stopPolling()
            return
        }

        timerId = setTimeout(() => poll(...args), interval)
    }

    // start polling
    const startPolling = (...args: T): void => {
        if (isPolling.value) return

        isPolling.value = true
        void poll(...args)
    }

    // stop polling
    const stopPolling = (): void => {
        isPolling.value = false
        if (timerId) {
            clearTimeout(timerId)
            timerId = null
        }
    }

    // avoid memory leak
    onUnmounted(stopPolling)

    return {
        isPolling,
        startPolling,
        stopPolling,
    }
}
